import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/browser";
import { createDefaultProfile } from "../reducers/spellbookData";

let upgrade = (state) => {
  console.log("Checking for upgrade...");

  let newState = state;

  switch (state.spellbookData.version) {
    case undefined:
      newState = version0(newState);

      break;
    case 1:
      newState = version1(newState);

      break;

    default:
      newState = state;
  }

  if (newState !== state) {
    newState = upgrade(newState);
    console.log("Upgraded", newState);
  }

  // Repair any profiles where `current` is missing
  newState = repairMissingCurrent(newState);

  // Validate that the selectedProfileId exists in profiles
  newState = validateSelectedProfile(newState);

  return newState;
};

/**
 * For upgrading from v0 to v1
 * @param {*} state
 * @returns
 */
function version0(state) {
  let newCustomSpells = {};
  if (typeof state.interfaceData.spellListCategories.Custom !== "undefined") {
    for (const [level, list] of Object.entries(
      state.interfaceData.spellListCategories.Custom
    )) {
      newCustomSpells[level] = [];
      for (let i = 0; i < list.length; i++) {
        newCustomSpells[level].push({
          spellId: list[i],
          isPrepared: false,
        });
      }
    }
  }

  let newProfiles = {};
  for (const [key, profile] of Object.entries(state.spellbookData.profiles)) {
    let newFavorites = {
      ...profile.favoriteSpells,
      Custom: {
        "lvl-0": [],
        "lvl-1": [],
        "lvl-2": [],
        "lvl-3": [],
        "lvl-4": [],
        "lvl-5": [],
        "lvl-6": [],
        "lvl-7": [],
        "lvl-8": [],
        "lvl-9": [],
      },
    };

    for (const [level, customList] of Object.entries(
      profile.favoriteSpells.Custom
    )) {
      for (let spell of customList) {
        newFavorites.Custom[level].push({ isPrepared: false, spellId: spell });
      }
    }

    let newLearnedSpells = {
      cantrip: [],
      first: [],
      second: [],
      third: [],
      fourth: [],
      fifth: [],
      sixth: [],
      seventh: [],
      eighth: [],
      ninth: [],
    };

    for (const [level, spellList] of Object.entries(profile.learnedSpells)) {
      newLearnedSpells[level] = spellList.map((element) => {
        return {
          ...element,
          isPrepared: false,
        };
      });
    }

    newProfiles[key] = {
      ...profile,
      customSpells: newCustomSpells,
      favoriteSpells: newFavorites,
      learnedSpells: newLearnedSpells,
    };
  }

  let newState = {
    ...state,
    spellbookData: {
      ...state.spellbookData,
      profiles: newProfiles,
      version: 1,
    },
    interfaceData: {
      ...state.interfaceData,
      spellListCategories: {
        ...state.interfaceData.spellListCategories,
        Custom: newCustomSpells,
      },
    },
  };

  return newState;
}

/**
 * For upgrading from v1 to v2
 * @param {*} state
 * @returns
 */
function version1(state) {
  // Update the profiles to include a type tag for cloud sync
  let newProfiles = {};
  for (const [key, profile] of Object.entries(state.spellbookData.profiles)) {
    newProfiles[key] = {
      profileVersion: 2,
      type: "local",
      version: 0,
      current: {
        ...profile,
      },
      old: {},
    };
  }

  let newState = {
    ...state,
    spellbookData: {
      ...state.spellbookData,
      profiles: newProfiles,
      version: 2,
    },
    interfaceData: {
      ...state.interfaceData,
      cloudSyncCountdown: 0,
      lastCloudSync: new Date().getTime(),
      isCloudSyncing: false,
    },
  };

  return newState;
}

/**
 * Validates that the selectedProfileId exists in profiles.
 * If it doesn't exist, selects the first profile.
 * If no profiles exist, creates a new default profile.
 * @param {*} state
 * @returns
 */
function validateSelectedProfile(state) {
  const { spellbookData } = state;
  const { profiles, selectedProfileId } = spellbookData;

  // Check if the selected profile exists
  if (profiles[selectedProfileId]) {
    // Everything is fine, return state as-is
    return state;
  }

  // Get the list of available profile IDs
  const profileIds = Object.keys(profiles);

  let newState = { ...state };

  // If there are no profiles at all, create a new default one
  if (profileIds.length === 0) {
    const newProfileId = uuidv4();

    newState = {
      ...state,
      spellbookData: {
        ...spellbookData,
        selectedProfileId: newProfileId,
        profiles: {
          [newProfileId]: createDefaultProfile("Spellbook 1", ["Wizard"]),
        },
      },
    };
    console.log("No profiles found, created default profile");
  } else {
    // Profiles exist, but the selected one doesn't - select the first one
    newState = {
      ...state,
      spellbookData: {
        ...spellbookData,
        selectedProfileId: profileIds[0],
      },
    };
    console.log(`Selected profile ${selectedProfileId} not found, switched to ${profileIds[0]}`);
  }

  return newState;
}

/**
 * Repairs profiles where `current` is undefined by falling back to `old`,
 * or a fresh default if both are missing. Reports to Sentry with full profile
 * data so we can diagnose how corruption occurred.
 */
function repairMissingCurrent(state) {
  const { spellbookData } = state;
  const { profiles } = spellbookData;

  let repaired = false;
  const newProfiles = { ...profiles };

  for (const [key, profile] of Object.entries(newProfiles)) {
    if (profile.current === undefined) {
      const repairedCurrent =
        profile.old !== undefined
          ? profile.old
          : createDefaultProfile().current;

      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setExtra("profileId", key);
        scope.setExtra("profileData", profile);
        Sentry.captureMessage(
          `Profile missing 'current' field — repaired from '${profile.old !== undefined ? "old" : "default"}'`
        );
      });

      newProfiles[key] = {
        ...profile,
        current: repairedCurrent,
      };
      repaired = true;
    }
  }

  if (!repaired) return state;

  return {
    ...state,
    spellbookData: {
      ...spellbookData,
      profiles: newProfiles,
    },
  };
}

export { upgrade };
